import * as React from 'react'

import { dimensions } from '@factorialco/deprecated-design-system'
import { BACKEND_DATE_FORMAT } from 'config/Vars'
import moment from 'moment'

import DayPicker from 'modules/core/components/DayPicker'
import Chip, { LabelChipProps } from 'modules/core/components/Filter/base/Chip'
import ClearValue from 'modules/core/components/Filter/base/ClearValue'
import Content from 'modules/core/components/Filter/base/Content'
import Provider from 'modules/core/components/Filter/base/Provider'
import useFilter from 'modules/core/components/Filter/base/useFilter'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import {
  FormatTypes,
  dateBackendToShortDMUI,
  dateBackendToUI,
  dateToString,
  stringToDate,
} from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

type SingleValue = string | undefined
type RangeValue = [string | undefined, string | undefined]
type AllValues = SingleValue | RangeValue

export type Props = LabelChipProps & {
  dateFormat?: FormatTypes
  header?: React.ReactNode
  defaultOpen?: boolean
  closeOnChange?: boolean
  noFooter?: boolean
  minDate?: moment.Moment
  maxDate?: moment.Moment
  onToggle?: (open: boolean) => void
  fullWidth?: boolean
} & (
    | {
        mode?: 'single'
        value?: SingleValue
        onValueChange: (value?: SingleValue) => void
      }
    | {
        mode: 'range'
        value: RangeValue
        onValueChange: (value: RangeValue) => void
      }
  )

function Body({ mode, minDate, maxDate }: Pick<Props, 'mode' | 'minDate' | 'maxDate'>) {
  const { value, onValueChange } = useFilter()

  // This value here is to save the range, in case it's incomplete, and not send it up through
  // onValueChange
  const [_value, setValue] = React.useState(value)

  // If value changes from top, let's set it here
  useUnsafeEffect(() => {
    setValue(value)
  }, [value])

  if (mode === 'range') {
    const onRangeValueChange = (newValue: RangeValue) => {
      const [firstDate, secondDate] = newValue

      const firstDateMoment = firstDate ? moment(firstDate) : undefined
      const secondDateMoment = secondDate ? moment(secondDate) : undefined

      const firstDateValue = firstDateMoment?.isValid()
        ? firstDateMoment.format(BACKEND_DATE_FORMAT)
        : undefined
      const secondDateValue = secondDateMoment?.isValid()
        ? secondDateMoment.format(BACKEND_DATE_FORMAT)
        : undefined

      setValue([firstDateValue, secondDateValue])
      if (firstDateValue && secondDateValue) {
        onValueChange([firstDateValue, secondDateValue])
      }
    }

    return (
      <DayPicker
        mode="range"
        label={i18n.t('time_tracking.settings.versions.effective_date')}
        value={_value as RangeValue}
        onChange={onRangeValueChange}
        showOnlyCalendar
        minDate={minDate}
        maxDate={maxDate}
      />
    )
  }

  const onSingleValueChange = (newDate: string) => {
    const newMoment = moment(newDate)
    const newDateValue = newMoment.isValid() ? newMoment.format(BACKEND_DATE_FORMAT) : undefined
    onValueChange(newDateValue)
  }

  return (
    <DayPicker
      label={i18n.t('time_tracking.settings.versions.effective_date')}
      value={value as string}
      onChange={onSingleValueChange}
      minDate={minDate}
      maxDate={maxDate}
      showOnlyCalendar
    />
  )
}

export default function DatePickerFilter({
  defaultOpen,
  onValueChange,
  onToggle,
  header,
  label,
  icon,
  value: valueProp,
  mode = 'single',
  closeOnChange,
  dateFormat,
  noFooter,
  minDate,
  maxDate,
  fullWidth,
}: Props) {
  const formatDate = (dateString: string) => {
    if (dateFormat) {
      const date = stringToDate(dateString, 'backend')

      return dateToString(date, dateFormat)
    }

    switch (mode) {
      case 'single':
        return dateBackendToUI(dateString)
      case 'range':
        return dateBackendToShortDMUI(dateString)
    }
  }

  const getActiveLabel = () => {
    switch (mode) {
      case 'single':
        return valueProp && formatDate(valueProp as string)
      case 'range':
        const firstDate = valueProp?.[0] ? formatDate(valueProp[0]) : ''
        const secondDate = valueProp?.[1] ? formatDate(valueProp[1]) : ''
        return `${firstDate} - ${secondDate}`
    }
  }

  const getValue = () => {
    switch (mode) {
      case 'single':
        return valueProp && dateToString(moment(valueProp as string), 'ui')
      case 'range':
        const firstDate = valueProp?.[0] ? dateToString(moment(valueProp[0]), 'backend') : undefined
        const secondDate = valueProp?.[1]
          ? dateToString(moment(valueProp[1]), 'backend')
          : undefined

        if (!firstDate && !secondDate) return undefined
        return [firstDate, secondDate] as [string | undefined, string | undefined]
    }
  }

  const activeLabel = getActiveLabel()
  const value = getValue()

  return (
    <Provider
      defaultOpen={!!defaultOpen}
      value={value as AllValues}
      onValueChange={onValueChange as (value: AllValues) => void}
      closeOnChange={closeOnChange}
    >
      <Chip.Toggler
        label={label}
        icon={icon}
        active={!!value}
        activeLabel={activeLabel}
        onToggle={onToggle}
        fullWidth={fullWidth}
      />
      <Content
        header={header}
        body={<Body mode={mode} minDate={minDate} maxDate={maxDate} />}
        footer={noFooter ? undefined : <ClearValue />}
        maxHeight={dimensions.s400}
      />
    </Provider>
  )
}
